import React from 'react';
import { ArrowRight, CheckCircle, Circle, Target, RefreshCw } from 'lucide-react';
import { CISA_FUNCTIONS, CISA_PILLARS, MATURITY_CRITERIA, PRODUCT_SUGGESTIONS } from '../../../config/cisaMaturityModel';
import { getDiagramTemplate, getLevelDiff } from '../data/maturityDiagramTemplates';
import JourneyDiagramCanvas from './JourneyDiagramCanvas';

/**
 * Maturity levels in order
 */
const MATURITY_LEVELS = [
  { key: 'TRADITIONAL', label: 'Traditional', color: 'var(--zt-traditional, #6b7280)', order: 0 },
  { key: 'INITIAL', label: 'Initial', color: 'var(--zt-initial, #eab308)', order: 1 },
  { key: 'ADVANCED', label: 'Advanced', color: 'var(--zt-advanced, #3b82f6)', order: 2 },
  { key: 'OPTIMAL', label: 'Optimal', color: 'var(--zt-optimal, #10b981)', order: 3 }
];

/**
 * JourneyGapAnalysis Component
 *
 * Side-by-side comparison of current vs target architectures
 * with roadmap for progression through intermediate levels.
 */
function JourneyGapAnalysis({
  functionCode,
  pillarCode,
  currentLevel,
  targetLevel,
  productAdoptions = {},
  onBack,
  onStartAssessment
}) {
  // Get function and pillar info
  const pillar = CISA_PILLARS.find(p => p.code === pillarCode);
  const fn = CISA_FUNCTIONS[pillarCode]?.find(f => f.code === functionCode);

  const currentTemplate = getDiagramTemplate(functionCode, currentLevel);
  const targetTemplate = getDiagramTemplate(functionCode, targetLevel);

  const currentInfo = MATURITY_LEVELS.find(l => l.key === currentLevel);
  const targetInfo = MATURITY_LEVELS.find(l => l.key === targetLevel);

  // Calculate steps needed
  const currentOrder = currentInfo?.order || 0;
  const targetOrder = targetInfo?.order || 0;
  const stepsNeeded = targetOrder - currentOrder;

  // Get intermediate levels
  const intermediateLevels = MATURITY_LEVELS.filter(
    l => l.order > currentOrder && l.order <= targetOrder
  );

  // Calculate diff between current and target
  const diff = getLevelDiff(functionCode, currentLevel, targetLevel);

  return (
    <div className="ztj-gap-analysis">
      {/* Header */}
      <div className="ztj-gap-header">
        <h2>Gap Analysis: {fn?.name || functionCode}</h2>
        <div className="ztj-gap-summary">
          <div
            className="ztj-gap-badge ztj-gap-badge--current"
            style={{ borderLeft: `3px solid ${currentInfo?.color}` }}
          >
            <span style={{ fontWeight: 600 }}>Current:</span>
            <span>{currentInfo?.label}</span>
          </div>
          <ArrowRight size={18} style={{ color: 'var(--color-text-subtle)' }} />
          <div
            className="ztj-gap-badge ztj-gap-badge--target"
            style={{ borderLeft: `3px solid ${targetInfo?.color}` }}
          >
            <span style={{ fontWeight: 600 }}>Target:</span>
            <span>{targetInfo?.label}</span>
          </div>
          <div className="ztj-gap-badge ztj-gap-badge--steps">
            <span style={{ fontWeight: 600 }}>{stepsNeeded}</span>
            <span>level{stepsNeeded !== 1 ? 's' : ''} to advance</span>
          </div>
        </div>
      </div>

      {/* Side-by-side diagrams */}
      <div className="ztj-gap-comparison">
        {/* Current state diagram */}
        <div className="ztj-gap-diagram-panel">
          <div
            className="ztj-gap-diagram-title"
            style={{ borderBottom: `2px solid ${currentInfo?.color}` }}
          >
            <Circle size={12} style={{ fill: currentInfo?.color, color: currentInfo?.color }} />
            Current: {currentTemplate?.name || currentInfo?.label}
          </div>
          <div style={{ padding: 'var(--space-2)' }}>
            <JourneyDiagramCanvas
              template={currentTemplate}
              showAnnotations={false}
              highlightNew={false}
              height={350}
            />
          </div>
        </div>

        {/* Arrow */}
        <div className="ztj-gap-arrow">
          <ArrowRight size={32} />
          <div className="ztj-gap-arrow-label">
            {stepsNeeded} step{stepsNeeded !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Target state diagram */}
        <div className="ztj-gap-diagram-panel">
          <div
            className="ztj-gap-diagram-title"
            style={{ borderBottom: `2px solid ${targetInfo?.color}` }}
          >
            <Target size={12} style={{ color: targetInfo?.color }} />
            Target: {targetTemplate?.name || targetInfo?.label}
          </div>
          <div style={{ padding: 'var(--space-2)' }}>
            <JourneyDiagramCanvas
              template={targetTemplate}
              showAnnotations={false}
              highlightNew={true}
              height={350}
            />
          </div>
        </div>
      </div>

      {/* Roadmap */}
      <div className="ztj-roadmap">
        <h3>Progression Roadmap</h3>
        <div className="ztj-roadmap-steps">
          {intermediateLevels.map((level, idx) => {
            const template = getDiagramTemplate(functionCode, level.key);
            const criteria = MATURITY_CRITERIA[functionCode]?.[level.key];
            const products = PRODUCT_SUGGESTIONS[functionCode]?.[level.key] || [];

            return (
              <div key={level.key} className="ztj-roadmap-step">
                <div
                  className="ztj-roadmap-step-number"
                  style={{ background: level.color }}
                >
                  {idx + 1}
                </div>
                <div className="ztj-roadmap-step-content">
                  <h4>
                    Progress to {level.label}
                    {template?.name && ` - ${template.name}`}
                  </h4>
                  <p>{criteria}</p>
                  {products.length > 0 && (
                    <p style={{ marginTop: 'var(--space-2)' }}>
                      <strong>Key technologies:</strong> {products.slice(0, 3).join(', ')}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* What's new section */}
      {diff && (diff.newNodes.length > 0 || diff.newEdges.length > 0) && (
        <div className="ztj-side-panel">
          <h3>
            <CheckCircle size={18} style={{ color: 'var(--color-success)' }} />
            New Components at Target Level
          </h3>
          <ul className="ztj-capabilities-list">
            {diff.newNodes.map(node => (
              <li key={node.id} className="ztj-capability-item">
                <CheckCircle size={14} />
                {node.label.replace('\n', ' ')}
                {node.category && (
                  <span style={{
                    marginLeft: 'var(--space-2)',
                    fontSize: '10px',
                    color: 'var(--color-text-subtle)',
                    textTransform: 'uppercase'
                  }}>
                    ({node.category})
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Actions */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: 'var(--space-3)',
        paddingTop: 'var(--space-3)'
      }}>
        <button
          type="button"
          className="ztj-action-btn"
          onClick={() => onStartAssessment('CURRENT')}
        >
          <RefreshCw size={16} />
          Reassess
        </button>
      </div>
    </div>
  );
}

export default JourneyGapAnalysis;
